/* src/components/testing/countingSpan/CountingSpanResults.js */
import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faExclamationTriangle, faRedo, faThList, faChartLine } from '@fortawesome/free-solid-svg-icons';

/**
 * Displays the results of the completed Counting Span task.
 */
const CountingSpanResults = ({ results, onDone, onRestart }) => {

    const {
        scorePCU = 0,
        maxScorePCU = 75, // Default from constants, but passed in for flexibility
        processingAccuracy = 0,
        accuracyThreshold = 85, // Default from constants
        flaggedLowAccuracy = false,
        isFamiliarization = false,
        error = null // Any error message from saving, etc.
    } = results || {}; // Destructure with defaults

    // Determine display styles based on accuracy
    const accuracyColor = flaggedLowAccuracy ? 'text-orange-600' : 'text-green-600';
    const accuracyIcon = flaggedLowAccuracy ? faExclamationTriangle : faCheckCircle;

    return (
        <div className="text-center max-w-md mx-auto p-4">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
                {isFamiliarization ? 'Familiarization Complete' : 'Assessment Complete'}
            </h2>

            {/* Display Error Message if present */}
            {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                    <strong>Error:</strong> {error}
                </div>
            )}

            {/* Score Display */}
            <div className="mb-5 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                <p className="text-sm font-medium text-indigo-700 mb-1">Recall Score (PCU)</p>
                <p className="text-4xl font-bold text-indigo-900">
                    {scorePCU} <span className="text-2xl text-indigo-500">/ {maxScorePCU}</span>
                </p>
                <p className="text-xs text-indigo-600 mt-1">(Items recalled in correct order)</p>
            </div>

            {/* Accuracy Display */}
            <div className={`mb-6 p-4 border rounded-lg ${flaggedLowAccuracy ? 'bg-orange-50 border-orange-200' : 'bg-green-50 border-green-200'}`}>
                <p className={`text-sm font-medium mb-1 ${accuracyColor}`}>Processing Accuracy</p>
                <p className={`text-3xl font-bold ${accuracyColor} flex items-center justify-center`}>
                    <FontAwesomeIcon icon={accuracyIcon} className="mr-2 text-2xl" />
                    {processingAccuracy.toFixed(1)}%
                </p>
                {flaggedLowAccuracy && (
                    <p className="text-xs text-orange-700 mt-1">
                        (Below {accuracyThreshold}% threshold - may indicate prioritizing recall over counting/judging)
                    </p>
                )}
                 {!flaggedLowAccuracy && (
                     <p className="text-xs text-green-700 mt-1">
                        (Good focus on the counting/judging task!)
                    </p>
                 )}
                 <p className="text-xs text-gray-500 mt-1">(Odd/Even judgments correct)</p>
            </div>

            {/* Familiarization Info */}
            {isFamiliarization && (
                <p className="text-sm text-gray-600 mb-6 italic">
                    This was a familiarization session. Scores may improve as you get used to the task format.
                </p>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-center gap-3">
                <button
                    onClick={onDone} // Navigate back to summary/dashboard
                    className="flex-1 sm:flex-none inline-flex items-center justify-center px-5 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                >
                    <FontAwesomeIcon icon={faThList} className="mr-2" />
                    View Summary
                </button>
                {/* Optional Restart Button */}
                 {onRestart && (
                    <button
                        onClick={onRestart} // Function to restart the task sequence
                        className="flex-1 sm:flex-none inline-flex items-center justify-center px-5 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                    >
                       <FontAwesomeIcon icon={faRedo} className="mr-2" />
                       Run Again
                    </button>
                )}
                {/* Optional: Link to Analytics */}
                {/* <button
                    onClick={() => navigate('/analytics')} // Assuming navigate is available or passed
                    className="flex-1 sm:flex-none inline-flex items-center justify-center px-5 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                >
                   <FontAwesomeIcon icon={faChartLine} className="mr-2" />
                   Analytics
                </button> */}
            </div>
        </div>
    );
};

CountingSpanResults.propTypes = {
    results: PropTypes.shape({
        scorePCU: PropTypes.number,
        maxScorePCU: PropTypes.number,
        processingAccuracy: PropTypes.number,
        accuracyThreshold: PropTypes.number,
        flaggedLowAccuracy: PropTypes.bool,
        isFamiliarization: PropTypes.bool,
        error: PropTypes.string, // Optional error message
    }).isRequired,
    onDone: PropTypes.func.isRequired, // Function to call when user clicks done/summary button
    onRestart: PropTypes.func, // Optional function to restart the task
};

export default CountingSpanResults;